import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Native design canvas — every fixed px/in value in the layout is authored
// against this size, then the whole thing is uniformly scaled to fit the
// container width so mobile and desktop look pixel-identical.
const DESIGN_WIDTH = 1100;   // px  (matches the outer maxWidth)
const DESIGN_HEIGHT = 778;   // px  (1100 / 1.414 → A4 landscape aspect)

export function fileUrl(path) {
  if (!path) return null;
  return `${BACKEND_URL}/api/files/${path}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

function isImageExt(path) {
  if (!path) return false;
  const p = path.toLowerCase();
  return p.endsWith(".png") || p.endsWith(".jpg") || p.endsWith(".jpeg") || p.endsWith(".webp") || p.endsWith(".svg");
}

function verifyUrl(certificateId) {
  if (typeof window === "undefined") return certificateId;
  return `${window.location.origin}/verify?cert=${encodeURIComponent(certificateId)}`;
}

// Fallback defaults when institution hasn't customized the red text fields yet.
const DEFAULT_TEXT_1 = "This is to certify that";
const DEFAULT_TEXT_2 = "has successfully completed the requirements for the degree of";
const DEFAULT_TEXT_3 = ""; // Optional signatory / honors line — hidden when empty

function buildCourse(certificate) {
  const deg = (certificate.degree || "").trim();
  const maj = (certificate.major || "").trim();
  if (!deg) return maj;
  if (!maj || maj === "—") return deg;
  return `${deg} in ${maj}`;
}

/**
 * A4 landscape certificate (297 × 210 mm → aspect ratio 1.414).
 * Layout follows the customer template:
 *   • Header image (green, editable by L1/L2)
 *   • Text Field 1 (red, per-institution)
 *   • Graduate Name (blue, system)
 *   • Text Field 2 (red, per-institution)
 *   • QR Code (left) | Course + Text Field 3 (right)
 *   • Certificate ID | Graduation Date
 *   • Signature 1 / Seal / Signature 3
 *   • Signature 2 /       / Signature 4
 * Existing `cert_template_image_path` is kept as a subtle background wash.
 */
export default function Certificate({ certificate, institution, animate = false, showQr = true }) {
  // Hooks MUST run on every render — declare them before any early return.
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setScale(w / DESIGN_WIDTH);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // Recompute on print too, so PDF output sits at the right size
    const onBeforePrint = () => update();
    window.addEventListener("beforeprint", onBeforePrint);
    return () => {
      ro.disconnect();
      window.removeEventListener("beforeprint", onBeforePrint);
    };
  }, []);

  if (!certificate || !institution) return null;

  const templateImagePath =
    institution.cert_template_image_path ||
    (isImageExt(institution.cert_template_path) ? institution.cert_template_path : null);
  const templateUrl = fileUrl(templateImagePath);

  const headerUrl = fileUrl(institution.header_image_path);
  const sig1Url = fileUrl(institution.signature_1_path);
  const sig2Url = fileUrl(institution.signature_2_path);
  const sig3Url = fileUrl(institution.signature_3_path);
  const sig4Url = fileUrl(institution.signature_4_path);
  const sealUrl = fileUrl(institution.seal_image_path);

  const text1 = institution.text_field_1 || DEFAULT_TEXT_1;
  const text2 = institution.text_field_2 || DEFAULT_TEXT_2;
  const text3 = institution.text_field_3 || DEFAULT_TEXT_3;
  const course = buildCourse(certificate);
  const qrData = verifyUrl(certificate.certificate_id);

  const hasLeftSigs = sig1Url || sig2Url;
  const hasRightSigs = sig3Url || sig4Url;
  const hasAnyFooter = hasLeftSigs || hasRightSigs || sealUrl;

  return (
    <motion.div
      ref={wrapperRef}
      initial={animate ? { opacity: 0, y: 20 } : false}
      animate={animate ? { opacity: 1, y: 0 } : false}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="certiseal-a4 relative bg-white border border-slate-200 shadow-[0_8px_40px_rgba(15,23,42,0.08)] rounded-2xl overflow-hidden mx-auto"
      data-testid="certificate-display"
      style={{ aspectRatio: "1.414 / 1", maxWidth: `${DESIGN_WIDTH}px`, width: "100%" }}
    >
      {/* Fixed-size design canvas — scaled proportionally to fit the wrapper */}
      <div
        className="cert-scale-inner absolute top-0 left-0"
        style={{
          width: `${DESIGN_WIDTH}px`,
          height: `${DESIGN_HEIGHT}px`,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
      {/* Optional uploaded template as full-opacity background */}
      {templateUrl && (
        <img
          src={templateUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          aria-hidden="true"
          data-testid="cert-template-bg"
        />
      )}

      <div className="relative flex flex-col h-full px-[5%] py-[4%]">
        {/* ── Header image / institution masthead ──────────────────── */}
        <div className="flex-shrink-0 flex items-center justify-center mt-[0.4in]" style={{ minHeight: "18%", maxHeight: "22%" }}>
          {headerUrl ? (
            <img
              src={headerUrl}
              alt=""
              className="max-h-full max-w-full object-contain mx-[0.34in]"
              data-testid="cert-header-image"
            />
          ) : (
            // Text-based masthead fallback — a verifier MUST always be able to
            // see which institution issued the credential, even if no header
            // image was uploaded (code review 2026-08).
            <div
              className="w-full text-center px-[0.5in]"
              data-testid="cert-header-image-empty"
              data-institution-id={institution.id}
            >
              <div
                className="font-display font-black text-slate-900 tracking-tight leading-tight"
                style={{ fontSize: "28px" }}
                data-testid="cert-institution-name"
              >
                {institution.name}
              </div>
              {institution.address && (
                <div
                  className="mt-1 text-slate-600 tracking-wide"
                  style={{ fontSize: "11px" }}
                  data-testid="cert-institution-address"
                >
                  {institution.address}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Text Field 1 (red) ───────────────────────────────────── */}
        <div className="text-center mt-[1.5%] text-slate-600" data-testid="cert-text-1"
          style={{ fontSize: "13px", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 600 }}
        >
          {text1}
        </div>

        {/* ── Graduate Name ────────────────────────────────────────── */}
        <div
          className="font-display font-extrabold text-slate-900 text-center mt-[1%] leading-tight tracking-tight mx-[0.4in]"
          style={{ fontSize: "32px" }}
          data-testid="cert-graduate-name"
        >
          {certificate.graduate_name}
        </div>

        {/* ── Text Field 2 (red) ───────────────────────────────────── */}
        <div
          className="font-serif-cert italic text-slate-700 text-center mt-0 max-w-[85%] mx-auto"
          style={{ fontSize: "15px" }}
          data-testid="cert-text-2"
        >
          {text2}
        </div>

        {/* ── QR (absolute, aligned with right signature) + Course (centered, full width) + Text Field 3 ─── */}
        {showQr && (
          <div
            className="absolute bg-white border border-slate-200 rounded-md p-1.5"
            data-testid="cert-qr-code"
            style={{ width: "10%", aspectRatio: "1 / 1", right: "calc(5% + 0.3in)", top: "55%" }}
          >
            <QRCodeSVG value={qrData} size={64} level="M" style={{ width: "100%", height: "100%" }} />
          </div>
        )}
        <div className="mt-0">
          <div
            className="font-display font-bold text-center mx-[0.4in]"
            style={{ fontSize: "30px", color: "rgb(171, 129, 22)" }}
            data-testid="cert-course"
          >
            {course}
          </div>
          {text3 && (
            <div
              className="text-slate-600 text-center mt-1 font-serif-cert italic mx-[0.4in]"
              style={{ fontSize: "15px" }}
              data-testid="cert-text-3"
            >
              {text3}
            </div>
          )}
        </div>

        {/* ── Certificate ID + Graduation Date ─────────────────────── */}
        <div className="mt-[1.5%] grid grid-cols-3 items-baseline px-[0.3in]">
          <div className="text-left" data-testid="cert-id-display">
            <div className="text-[9px] uppercase tracking-widest font-semibold text-slate-400">Certificate ID</div>
            <div className="font-mono text-slate-800" style={{ fontSize: "15px" }}>
              {certificate.certificate_id}
            </div>
          </div>
          <div className="text-center" data-testid="cert-graduation-date">
            <div className="text-[9px] uppercase tracking-widest font-semibold text-slate-400">Graduation Date</div>
            <div className="font-display font-semibold text-slate-800" style={{ fontSize: "15px" }}>
              {fmtDate(certificate.graduation_date)}
            </div>
          </div>
          <div aria-hidden="true" />
        </div>

        {/* ── Footer: signatures + seal ────────────────────────────── */}
        <div className="mt-auto pt-[2%] flex items-start justify-between gap-4">
          {/* Left column — Sig 1 (top) + Sig 2 (bottom) */}
          <div className="flex-1 flex flex-col items-start gap-2 mt-0 ml-[0.3in]" style={{ maxWidth: "28%" }}>
            {sig1Url && (
              <img src={sig1Url} alt="" className="max-h-[80px] max-w-full object-contain mt-[0.1in]" data-testid="cert-sig-1" />
            )}
            {sig2Url && (
              <img src={sig2Url} alt="" className="max-h-[80px] max-w-full object-contain" data-testid="cert-sig-2" />
            )}
          </div>
          {/* Center — Seal */}
          <div className="flex-shrink-0 flex items-end justify-center" style={{ maxWidth: "20%" }}>
            {sealUrl && (
              <img
                src={sealUrl}
                alt=""
                className="max-h-[130px] object-contain mb-[1in]"
                data-testid="cert-seal"
                style={{ maxWidth: "160px" }}
              />
            )}
          </div>
          {/* Right column — Sig 3 (top) + Sig 4 (bottom) */}
          <div className="flex-1 flex flex-col items-end gap-2 mr-[0.3in]" style={{ maxWidth: "28%" }}>
            {sig3Url && (
              <img src={sig3Url} alt="" className="max-h-[80px] max-w-full object-contain" data-testid="cert-sig-3" />
            )}
            {sig4Url && (
              <img src={sig4Url} alt="" className="max-h-[80px] max-w-full object-contain" data-testid="cert-sig-4" />
            )}
          </div>
        </div>
        {/* Reserve blank space if no footer imagery is uploaded */}
        {!hasAnyFooter && <div className="mt-auto pt-[6%]" aria-hidden="true" />}
      </div>
      </div>
    </motion.div>
  );
}
